import type { BackofficeReportChangeSignal } from './reportChangeSignal';
import {
  buildReportReadFamilyInvalidationTags,
  sortReportReadInvalidationTags,
  type ReportReadInvalidationTag,
  type ReportReadInvalidationTagType,
} from './reportReadFamilyPolicy';

const INVALIDATION_COALESCE_WINDOW_MS = 250;

export const buildReportReadInvalidationTags = (
  signal: BackofficeReportChangeSignal
): ReportReadInvalidationTag[] => buildReportReadFamilyInvalidationTags(signal);

export type InvalidateTags = (tags: ReportReadInvalidationTag[]) => unknown;

type ReportPresenceLostPayload = {
  reportId: string;
  reason: 'reportPresenceLost';
};

type ReportReadInvalidationCoordinatorOptions = {
  onReportPresenceLost?: (payload: ReportPresenceLostPayload) => void;
};

export type ReportReadInvalidationCoordinator = ((signal: BackofficeReportChangeSignal) => void) & {
  cancel: () => void;
};

export const createReportReadInvalidationCoordinator = (
  invalidateTags: InvalidateTags,
  options: ReportReadInvalidationCoordinatorOptions = {}
): ReportReadInvalidationCoordinator => {
  let flushTimeoutId: ReturnType<typeof setTimeout> | null = null;
  const pendingTags = new Map<ReportReadInvalidationTagType, ReportReadInvalidationTag>();
  const pendingChangeKinds = new Set<BackofficeReportChangeSignal['changeKind']>();
  let pendingSignalCount = 0;
  let pendingPresenceLossCount = 0;

  const resetPendingDiagnostics = (): void => {
    pendingChangeKinds.clear();
    pendingSignalCount = 0;
    pendingPresenceLossCount = 0;
  };

  const flushPendingInvalidations = (): void => {
    if (flushTimeoutId) {
      clearTimeout(flushTimeoutId);
      flushTimeoutId = null;
    }

    if (pendingTags.size === 0) {
      resetPendingDiagnostics();
      return;
    }

    const tags = sortReportReadInvalidationTags([...pendingTags.values()]);
    pendingTags.clear();
    const signalCount = pendingSignalCount;
    const presenceLossCount = pendingPresenceLossCount;
    const changeKinds = [...pendingChangeKinds].sort();
    resetPendingDiagnostics();

    console.log('[reports-signal] invalidation flush', {
      signalCount,
      presenceLossCount,
      changeKinds,
      tagTypes: tags.map((tag) => tag.type),
      tagCount: tags.length,
    });
    invalidateTags(tags);
  };

  const coordinate = (signal: BackofficeReportChangeSignal): void => {
    pendingSignalCount += 1;
    pendingChangeKinds.add(signal.changeKind);

    if (signal.changeKind === 'deletion' && signal.changedFacts.includes('reportPresence')) {
      pendingPresenceLossCount += 1;
      options.onReportPresenceLost?.({
        reportId: signal.reportId,
        reason: 'reportPresenceLost',
      });
    }

    buildReportReadInvalidationTags(signal).forEach((tag) => {
      pendingTags.set(tag.type, tag);
    });

    if (flushTimeoutId) {
      clearTimeout(flushTimeoutId);
    }

    flushTimeoutId = setTimeout(flushPendingInvalidations, INVALIDATION_COALESCE_WINDOW_MS);
  };

  coordinate.cancel = (): void => {
    if (flushTimeoutId) {
      clearTimeout(flushTimeoutId);
      flushTimeoutId = null;
    }
    pendingTags.clear();
    resetPendingDiagnostics();
  };

  return coordinate;
};
